<script setup>
import { ref, onMounted, computed } from 'vue';
import { Switch } from '@/components/ui/switch';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { ChevronDown, ChevronUp } from 'lucide-vue-next';
import { useNovuStore } from '@/stores/novu.store';
import { novu_alert_channels, novu_alert_channel_icons } from '../../utils/utils.js';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const loading = ref(true);
const userPreferences = ref([]);
// Track individual toggle loading states using a unique key per template-channel.
const toggleLoading = ref({});
const novuStore = useNovuStore();

onMounted(async () => {
    const fetchedUserPreferences = await novuStore.fetchUserPreferences();
    if (fetchedUserPreferences && novuStore.userPreferences) {
        userPreferences.value = novuStore.userPreferences.map((preference) => ({
            ...preference,
            expanded: false
        }));
    }
    loading.value = false;
});

function resolveChannelName(channelCode) {
    const channelName = novu_alert_channels[channelCode] || channelCode;
    // Add hardcoded "Chat & Webhook" for Chat channels
    if (channelName.toLowerCase().trim() === 'chat') {
        return 'Chat & Webhook';
    }
    return channelName;
}

function resolveChannelIcon(channelCode) {
    return novu_alert_channel_icons[channelCode] || channelCode;
}

function getChannelNames(channels) {
    return Object.entries(channels)
        .filter(([_, enabled]) => enabled)
        .map(([channel]) => resolveChannelName(channel))
        .join(', ');
}

function toggleTemplate(templateId) {
    userPreferences.value = userPreferences.value.map((pref) => {
        if (pref.template._id === templateId) {
            return { ...pref, expanded: !pref.expanded };
        }
        return pref;
    });
}

const sortedTemplates = computed(() => {
    return userPreferences.value
        .filter((item) => item.preference.enabled)
        .map((item) => ({
            id: item.template._id,
            name: item.template.name,
            channels: item.preference.channels,
            expanded: item.expanded
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

async function toggleChannelStatus(templateId, channelType, newValue = null) {
    const template = userPreferences.value.find((pref) => pref.template._id === templateId);
    if (!template) return;

    // If newValue is passed (from @update:model-value), use it; otherwise toggle current value
    const currentValue = template.preference.channels[channelType];
    const finalValue = newValue !== null ? newValue : !currentValue;
    const key = `${templateId}-${channelType}`;

    // Set loading state for this toggle.
    toggleLoading.value[key] = true;
    try {
        const preference = {
            templateId,
            channelType,
            checked: finalValue
        };

        const updated = await novuStore.updateUserPreference(preference);

        if (updated) {
            userPreferences.value = userPreferences.value.map((pref) => {
                if (pref.template._id === templateId) {
                    return {
                        ...pref,
                        preference: {
                            ...pref.preference,
                            channels: {
                                ...pref.preference.channels,
                                [channelType]: finalValue
                            }
                        }
                    };
                }
                return pref;
            });
        } else {
            console.error('Failed to update channel status.');
        }
    } catch (error) {
        console.error('Error updating channel status:', error);
    } finally {
        toggleLoading.value[key] = false;
    }
}
</script>

<template>
    <div v-if="loading" class="integrations-list overflow-auto max-h-60-dynamic p-2 space-y-4">
        <!-- Alert preferences skeleton -->
        <div v-for="n in 4" :key="`skeleton-${n}`">
            <Card>
                <CardHeader>
                    <div class="flex items-center justify-between">
                        <div class="flex-1 min-w-0">
                            <SkeletonLoader class="h-5 w-48 mb-3" :loading="true" />
                            <div class="flex flex-wrap gap-1">
                                <SkeletonLoader class="h-5 w-16" :loading="true" />
                                <SkeletonLoader class="h-5 w-20" :loading="true" />
                                <SkeletonLoader class="h-5 w-14" :loading="true" />
                            </div>
                        </div>
                        <SkeletonLoader type="circle" class="h-5 w-5 shrink-0" :loading="true" />
                    </div>
                </CardHeader>
            </Card>
        </div>
    </div>
    <div v-else class="integrations-list overflow-auto max-h-60-dynamic p-2 space-y-4">
        <Card v-for="template in sortedTemplates" :key="template.id">
            <CardHeader>
                <Button variant="ghost" class="w-full justify-between p-0 h-auto" @click="toggleTemplate(template.id)">
                    <div class="text-left flex-1 min-w-0">
                        <div class="flex items-center">
                            <span class="text-base font-medium truncate">{{ template.name }}</span>
                        </div>
                        <div class="flex flex-wrap gap-1 mt-2">
                            <Badge
                                v-for="channel in Object.keys(template.channels).filter((c) => template.channels[c])"
                                :key="channel"
                                variant="default"
                                class="text-xs">
                                {{ resolveChannelName(channel) }}
                            </Badge>
                        </div>
                    </div>
                    <ChevronDown v-if="!template.expanded" class="h-5 w-5 text-muted-foreground" />
                    <ChevronUp v-else class="h-5 w-5 text-muted-foreground" />
                </Button>
            </CardHeader>
            <CardContent v-if="template.expanded" class="pt-0">
                <div class="border-t pt-4 space-y-3">
                    <div v-for="(enabled, channel) in template.channels" :key="`channel-${channel}`" class="flex items-center justify-between py-2">
                        <div class="flex items-center gap-3">
                            <img :src="resolveChannelIcon(channel)" class="w-8 h-8 opacity-60" />
                            <span class="text-sm font-medium">{{ resolveChannelName(channel) }}</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <Switch
                                :model-value="template.channels[channel]"
                                :disabled="toggleLoading[`${template.id}-${channel}`]"
                                @update:model-value="(newValue) => toggleChannelStatus(template.id, channel, newValue)" />
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    </div>
</template>
